import "antd/dist/antd.css";
import "../App.css";

import { useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Button } from "antd";
import {
    ArrowLeftOutlined,
    CarOutlined,
    EnvironmentOutlined,
    HomeOutlined,
    PushpinOutlined,
} from "@ant-design/icons";
import { Link, useLoaderData } from "react-router-dom";
import { apiFetch } from "../apiFetch";
import { goToApp } from "../siteLinks";
import GuideCtaBand from "../components/GuideCtaBand";
import GuideSection from "../components/GuideSection";
import ItineraryMap from "../components/ItineraryMap";
import RelatedGuides from "../components/RelatedGuides";
import NavigateIcon from "../components/NavigateIcon";
import MapPinIcon from "../components/MapPinIcon";
import {
    buildMapsLink,
    buildPlaceLink,
    buildDirectionsFromCurrentLink,
    hasAccommodation,
} from "../itineraryRoutes";
import {
    bookingSearchUrl,
    hotelsSearchUrl,
    pitchupSearchUrl,
    airbnbSearchUrl,
} from "../cjLink";
import usePageMeta from "../usePageMeta";
import { jsonLdString } from "../jsonLd";

const MAP_ENABLED = process.env.REACT_APP_MAP_ENABLED !== "false";

// Day-rail accent — a single teal for every day's number chip and place
// chip. The design system allows per-day rust accents for "pass/marquee"
// days, but those aren't derivable from loader data, so we keep it uniform.
const DAY_ACCENT = {
    fill: "#0f4c5c",
    chipBg: "rgba(15,76,92,.12)",
    chipText: "#0f4c5c",
} as const;

// Meta-chip palette, keyed to the field the chip carries so the hero row
// reads at a glance: region/country teal, duration rust, difficulty green,
// season plum — the same --gt-feature-* scheme the guide rails use.
const CHIP = {
    teal: { background: "rgba(15,76,92,.12)", color: "#0f4c5c" },
    rust: { background: "rgba(193,101,74,.13)", color: "#a1462c" },
    green: { background: "rgba(91,138,114,.14)", color: "#3f6b53" },
    plum: { background: "rgba(122,79,109,.14)", color: "#5f3c54" },
} as const;

// Flag + display name for the countries a European tour is likely to cross.
// The loader gives `country` as an ISO-3166 alpha-2 code (e.g. "IT", "GB"),
// so we key on that; a full-name fallback keeps older data working too.
const COUNTRY: Record<string, { flag: string; name: string }> = {
    GB: { flag: "🇬🇧", name: "United Kingdom" },
    FR: { flag: "🇫🇷", name: "France" },
    CH: { flag: "🇨🇭", name: "Switzerland" },
    IT: { flag: "🇮🇹", name: "Italy" },
    DE: { flag: "🇩🇪", name: "Germany" },
    AT: { flag: "🇦🇹", name: "Austria" },
    BE: { flag: "🇧🇪", name: "Belgium" },
    NL: { flag: "🇳🇱", name: "Netherlands" },
    LU: { flag: "🇱🇺", name: "Luxembourg" },
    ES: { flag: "🇪🇸", name: "Spain" },
    PT: { flag: "🇵🇹", name: "Portugal" },
    IE: { flag: "🇮🇪", name: "Ireland" },
    AD: { flag: "🇦🇩", name: "Andorra" },
    MC: { flag: "🇲🇨", name: "Monaco" },
    LI: { flag: "🇱🇮", name: "Liechtenstein" },
    SI: { flag: "🇸🇮", name: "Slovenia" },
    HR: { flag: "🇭🇷", name: "Croatia" },
    CZ: { flag: "🇨🇿", name: "Czech Republic" },
    PL: { flag: "🇵🇱", name: "Poland" },
    DK: { flag: "🇩🇰", name: "Denmark" },
    NO: { flag: "🇳🇴", name: "Norway" },
    SE: { flag: "🇸🇪", name: "Sweden" },
    GR: { flag: "🇬🇷", name: "Greece" },
};
const lookupCountry = (c?: string) => {
    if (!c) return undefined;
    const key = c.trim();
    if (COUNTRY[key.toUpperCase()]) return COUNTRY[key.toUpperCase()];
    return Object.values(COUNTRY).find(
        (v) => v.name.toLowerCase() === key.toLowerCase(),
    );
};
const countryFlag = (c?: string) => lookupCountry(c)?.flag || "";
// Chip label: "🇮🇹 Italy" when recognised, else the raw value as given.
const countryLabel = (c?: string) => {
    const m = lookupCountry(c);
    return m ? `${m.flag} ${m.name}` : c || "";
};

// Transport-method glyph for the "at a glance" stat card.
const TRANSPORT_GLYPH: Record<string, string> = {
    motorbike: "🏍",
    motorcycle: "🏍",
    bike: "🚲",
    bicycle: "🚲",
    cycle: "🚲",
    cycling: "🚲",
    car: "🚗",
    campervan: "🚐",
    van: "🚐",
};
const transportGlyph = (m?: string) =>
    (m && TRANSPORT_GLYPH[m.trim().toLowerCase()]) || "🏍";

// First sentence of a day's description — the one-line rail summary.
const firstSentence = (text?: string): string => {
    if (!text) return "";
    const t = text.trim();
    const m = t.match(/^.*?[.!?](\s|$)/);
    return (m ? m[0] : t).trim();
};

// The "What you get when you clone" cards — the only new static copy on the
// page (everything else is loader data), lifted from the design prototype.
const CLONE_BENEFITS = [
    {
        marker: "🗺",
        title: "The full day-by-day plan",
        body: "Every day, stop and overnight area copies across, editable leg by leg. Reorder days, add rest days, swap a pass.",
    },
    {
        marker: "📅",
        title: "Your dates & your group",
        body: "Set the start date and the days snap to real dates. Invite your mates and everyone sees the same plan.",
    },
    {
        marker: "💷",
        title: "Shared expenses & checklists",
        body: "Fuel, tolls and accommodation roll into a trip budget split across the group in every currency, with a shared packing checklist.",
    },
    {
        marker: "🧭",
        title: "Live route links & weather",
        body: "Every leg keeps its Google-Maps routing, and the app layers in day-by-day weather once your dates are set.",
    },
];

type Stop = { title?: string; address?: string; notes?: string };
type Day = {
    itineraryIdentifier: string;
    order: number;
    title?: string;
    description?: string;
    accommodation?: {
        title?: string;
        address?: string;
        url?: string;
        notes?: string;
    };
    stops?: Stop[];
    notes?: string;
};

type Template = {
    identifier: string;
    slug: string;
    name: string;
    summary?: string;
    description?: string;
    region?: string;
    country?: string;
    transportMethod?: string;
    durationDays?: number;
    difficulty?: string;
    season?: string;
    heroImageUrl?: string | null;
    authorName?: string;
};

export const templateLoader = async ({ params }: { params: any }) => {
    const slug = params.slug;
    const response = await apiFetch(`/tour-templates/${encodeURIComponent(slug)}`);
    if (!response.ok) throw new Error(`Failed to load template: ${response.status}`);
    const result = await response.json();
    return { template: result.template as Template, days: (result.days || []) as Day[] };
};

function TourTemplate() {
    const { template, days } = useLoaderData() as { template: Template; days: Day[] };
    const [mapOpen, setMapOpen] = useState(false);

    const mapDays = useMemo(
        () =>
            days.map((d) => ({
                itineraryIdentifier: d.itineraryIdentifier,
                title: d.title || "",
                notes: d.notes || "",
                accommodation: {
                    title: d.accommodation?.title || "",
                    address: d.accommodation?.address || "",
                    url: "",
                    notes: d.accommodation?.notes || "",
                },
                stops: (d.stops || []).map((s, i) => ({
                    key: `${d.itineraryIdentifier}-stop-${i}`,
                    title: s.title || "",
                    address: s.address || "",
                    notes: s.notes || "",
                })),
            })),
        [days],
    );

    const hasMapData = mapDays.some(
        (d) => d.accommodation.address || d.stops.some((s) => s.address),
    );
    const canonicalUrl = `https://www.planamoto.com/explore/${template.slug}`;
    const metaDescription =
        template.summary ||
        `${template.name} — a day-by-day ${template.transportMethod || "road"} tour you can clone and customise on Planamoto.`;
    usePageMeta({
        title: `${template.name} — ${template.durationDays || ""} day motorbike tour | Planamoto`,
        description: metaDescription,
        image: template.heroImageUrl || undefined,
        url: canonicalUrl,
    });

    const jsonLd = useMemo(() => {
        const ld: Record<string, any> = {
            "@context": "https://schema.org",
            "@type": "TouristTrip",
            name: template.name,
            description: metaDescription,
            url: canonicalUrl,
            touristType: "Motorcycle riders",
            provider: {
                "@type": "Organization",
                name: "Planamoto",
                url: "https://www.planamoto.com",
            },
        };
        if (template.heroImageUrl) ld.image = template.heroImageUrl;
        if (template.durationDays) {
            ld.duration = `P${template.durationDays}D`; // ISO 8601 duration
        }
        const subjectOf = [template.region, template.country].filter(Boolean).join(", ");
        if (subjectOf) {
            ld.location = {
                "@type": "Place",
                name: subjectOf,
            };
        }
        if (days.length > 0) {
            ld.itinerary = {
                "@type": "ItemList",
                numberOfItems: days.length,
                itemListElement: days.map((d, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    item: {
                        "@type": "TouristTrip",
                        name: d.title || `Day ${i + 1}`,
                        ...(d.description ? { description: d.description } : {}),
                    },
                })),
            };
        }
        return ld;
    }, [template, days, metaDescription, canonicalUrl]);

    // Breadcrumb LD: Home → Explore tours → <this template>. Lets Google
    // render a breadcrumb in the search snippet instead of the raw URL.
    const breadcrumbLd = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.planamoto.com" },
            { "@type": "ListItem", position: 2, name: "Explore tours", item: "https://www.planamoto.com/explore" },
            { "@type": "ListItem", position: 3, name: template.name, item: canonicalUrl },
        ],
    }), [template.name, canonicalUrl]);

    const handleClone = () => {
        // The clone flow is gated and only exists on the app. Hop across
        // origins; the app's loader will redirect to login if needed and
        // bounce back here after auth.
        goToApp(`/explore/${template.slug}/clone`);
    };

    const dayCount = template.durationDays || days.length;
    const totalStops = days.reduce(
        (n, d) =>
            n +
            (Array.isArray(d.stops)
                ? d.stops.filter((s) => (s.title || s.address || "").trim()).length
                : 0),
        0,
    );
    const overnightCount = days.filter((d) =>
        hasAccommodation(d.accommodation || {}),
    ).length;

    // Hero meta chips, coloured by field type. Only present fields render.
    const metaChips: { label: string; style: { background: string; color: string } }[] = [];
    if (template.region) metaChips.push({ label: template.region, style: CHIP.teal });
    if (template.country)
        metaChips.push({ label: countryLabel(template.country), style: CHIP.teal });
    if (template.durationDays)
        metaChips.push({ label: `${template.durationDays} days`, style: CHIP.rust });
    if (template.difficulty)
        metaChips.push({ label: template.difficulty, style: CHIP.green });
    if (template.season)
        metaChips.push({ label: template.season, style: CHIP.plum });

    // "At a glance" stat cards, computed from what the loader actually
    // provides — drop any stat we can't derive rather than invent it.
    const stats: { value: string; label: string }[] = [];
    if (dayCount) stats.push({ value: String(dayCount), label: "days" });
    if (totalStops)
        stats.push({
            value: String(totalStops),
            label: totalStops === 1 ? "stop" : "stops",
        });
    if (overnightCount)
        stats.push({
            value: String(overnightCount),
            label: overnightCount === 1 ? "overnight" : "overnights",
        });
    if (template.transportMethod)
        stats.push({
            value: transportGlyph(template.transportMethod),
            label: template.transportMethod,
        });

    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(jsonLd) }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(breadcrumbLd) }}
            />

            {/* 1 — Hero (cream), reusing the shared guide hero band shell */}
            <div className="gt-guide-hero-band">
                <div className="gt-guide-hero-band__inner">
                    <div className="gt-guide-hero-band__head">
                        <Link to="/explore" className="gt-tour-template__back">
                            <ArrowLeftOutlined /> Explore tours
                        </Link>
                        {metaChips.length > 0 && (
                            <div className="gt-tt-hero-chips">
                                {metaChips.map((c, i) => (
                                    <span
                                        key={i}
                                        className="gt-guide-rail__chip"
                                        style={{
                                            background: c.style.background,
                                            color: c.style.color,
                                        }}
                                    >
                                        {c.label}
                                    </span>
                                ))}
                            </div>
                        )}
                        <h1 className="gt-h1 gt-guide-article__title">
                            {template.name}
                        </h1>
                        <div className="gt-tt-hero-cta">
                            <Button type="primary" size="large" onClick={handleClone}>
                                Clone to my tours
                            </Button>
                            <span className="gt-tt-hero-note">
                                Free · customise every leg after cloning
                            </span>
                        </div>
                    </div>
                    {template.summary && (
                        <p className="gt-guide-article__lede gt-guide-hero-band__lede">
                            {template.summary}
                        </p>
                    )}
                    <div className="gt-guide-hero-band__photo">
                        {template.heroImageUrl ? (
                            <img src={template.heroImageUrl} alt="" />
                        ) : (
                            <div className="gt-guide-hero-band__photo-placeholder">
                                <span>PHOTO — {template.name}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 2 — "The template at a glance" dark-teal lead */}
            <section className="gt-guide-opinion">
                <svg
                    className="gt-guide-opinion__route"
                    viewBox="0 0 900 60"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                >
                    <path d="M-10 40 Q 220 8 460 26 T 920 16" />
                </svg>
                <div className="gt-guide-opinion__inner">
                    <div className="gt-eyebrow gt-guide-opinion__eyebrow">
                        The template at a glance
                    </div>
                    <h2 className="gt-guide-opinion__heading">
                        Everything comes across when you clone
                    </h2>
                    <p className="gt-guide-opinion__intro">
                        The day-by-day plan, overnight areas, recommended stops and
                        route links land in your own tours ready to edit. Set your
                        dates, invite the group, and tweak any leg.
                    </p>
                    {template.description && (
                        <p
                            className="gt-guide-opinion__intro"
                            style={{ whiteSpace: "pre-wrap" }}
                        >
                            {template.description}
                        </p>
                    )}
                    {stats.length > 0 && (
                        <div
                            className="gt-tt-stats"
                            style={
                                {
                                    "--tt-cols": Math.min(stats.length, 5),
                                } as CSSProperties
                            }
                        >
                            {stats.map((s) => (
                                <div key={s.label} className="gt-guide-stack__col">
                                    <div className="gt-guide-trip-stats__value">
                                        {s.value}
                                    </div>
                                    <div className="gt-eyebrow gt-guide-stack__label">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div
                        className="gt-guide-cta-band__buttons"
                        style={{ justifyContent: "flex-start", marginTop: 24 }}
                    >
                        <Button type="primary" size="large" onClick={handleClone}>
                            Clone to my tours
                        </Button>
                        {MAP_ENABLED && hasMapData && (
                            <Button
                                size="large"
                                icon={<EnvironmentOutlined />}
                                onClick={() => setMapOpen(true)}
                            >
                                See on map
                            </Button>
                        )}
                    </div>
                </div>
            </section>

            {/* 3 — "Day by day" transition band */}
            <div className="gt-guide-transition">
                <div className="gt-eyebrow gt-guide-transition__eyebrow">
                    Day by day
                </div>
                <h2 className="gt-guide-transition__heading">
                    {dayCount
                        ? `${dayCount} days, planned leg by leg.`
                        : "Planned leg by leg."}
                </h2>
                <p className="gt-guide-transition__intro">
                    Each day has a route, recommended stops and an overnight area —
                    all editable once it's in your tours.
                </p>
            </div>

            {/* 4 — Day rails, one per loader day */}
            {days.map((day, idx) => {
                const prevDay = idx > 0 ? days[idx - 1] : null;
                const stops = Array.isArray(day.stops) ? day.stops : [];
                const accommodation = day.accommodation || {};
                const accAddress = (accommodation.address || "").trim();
                const prevAddress = (prevDay?.accommodation?.address || "").trim();
                const showAccommodation = hasAccommodation(accommodation);

                const stopsWithAddresses = stops.filter(
                    (s) => (s.address || "").trim(),
                );
                const lastStopWithAddress =
                    stopsWithAddresses.length > 0
                        ? stopsWithAddresses[stopsWithAddresses.length - 1]
                        : null;
                const drivingOriginAddress = lastStopWithAddress
                    ? lastStopWithAddress.address
                    : prevAddress;
                const drivingLink =
                    drivingOriginAddress && accAddress
                        ? buildMapsLink(
                              drivingOriginAddress,
                              accAddress,
                              [],
                              template.transportMethod,
                          )
                        : null;
                const stopsRouteLink =
                    !drivingLink && stopsWithAddresses.length >= 2
                        ? buildMapsLink(
                              stopsWithAddresses[0].address,
                              stopsWithAddresses[stopsWithAddresses.length - 1]
                                  .address,
                              stopsWithAddresses.slice(1, -1).map((s) => s.address),
                              template.transportMethod,
                          )
                        : null;
                const drivingOriginLabel = lastStopWithAddress
                    ? lastStopWithAddress.title || lastStopWithAddress.address
                    : "Last night's accommodation";
                const accommodationMapsLink = buildPlaceLink(accAddress);
                const accommodationFromCurrent = buildDirectionsFromCurrentLink(
                    accAddress,
                    template.transportMethod,
                );
                // "Find a place to stay" search links for the overnight town —
                // mirrors the in-app itinerary's four accommodation buttons.
                const staySid = `template_${template.slug}_day${idx + 1}`;
                const stayBookingLink = bookingSearchUrl(accAddress, staySid);
                const stayHotelsLink = hotelsSearchUrl(accAddress, staySid);
                const stayPitchupLink = pitchupSearchUrl(accAddress);
                const stayAirbnbLink = airbnbSearchUrl(accAddress);

                // Place chip: the overnight town — the hotel name or its
                // address (last comma-segment) → else the first stop's title
                // → else a sensible fallback. Prefixed with the country flag.
                const firstStopTitle =
                    stops.find((s) => (s.title || "").trim())?.title || "";
                const placeSource = (
                    (accommodation.title || "").trim() ||
                    accAddress ||
                    firstStopTitle
                ).trim();
                const place = placeSource
                    ? placeSource
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                          .pop() || placeSource
                    : "";
                const placeLabel =
                    place || template.region || `Day ${idx + 1}`;

                return (
                    <GuideSection
                        key={day.itineraryIdentifier}
                        index={idx}
                        day={{
                            catnum: String(idx + 1).padStart(2, "0"),
                            flag: countryFlag(template.country),
                            place: placeLabel,
                            title: day.title || `Day ${idx + 1}`,
                            summary: firstSentence(day.description),
                            accent: DAY_ACCENT,
                        }}
                    >
                        {day.description && (
                            <p style={{ whiteSpace: "pre-wrap" }}>
                                {day.description}
                            </p>
                        )}

                        {stops.length > 0 && (
                            <div
                                style={{
                                    marginTop: 8,
                                    paddingTop: 14,
                                    borderTop: "1px dashed var(--gt-border)",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                }}
                            >
                                {stopsRouteLink && (
                                    <div
                                        className="gt-body-sm"
                                        style={{ marginBottom: 4 }}
                                    >
                                        <CarOutlined style={{ marginRight: 6 }} />
                                        <span>
                                            {`Stop 1 → Stop ${stopsWithAddresses.length}`}
                                        </span>
                                        {" · "}
                                        <a
                                            href={stopsRouteLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Open in Google Maps
                                        </a>
                                    </div>
                                )}
                                {stops.map((s, i) => {
                                    const stopQuery = s.address || s.title || "";
                                    const stopMaps = buildPlaceLink(stopQuery);
                                    const stopFromCurrent =
                                        buildDirectionsFromCurrentLink(
                                            stopQuery,
                                            template.transportMethod,
                                        );
                                    const stopDestAddress = (s.address || "").trim();
                                    const stopOriginAddress =
                                        i === 0
                                            ? prevAddress
                                            : (stops[i - 1]?.address || "").trim();
                                    const stopRouteLink =
                                        stopOriginAddress && stopDestAddress
                                            ? buildMapsLink(
                                                  stopOriginAddress,
                                                  stopDestAddress,
                                                  [],
                                                  template.transportMethod,
                                              )
                                            : null;
                                    const stopOriginLabel =
                                        i === 0
                                            ? "Last night's accommodation"
                                            : stops[i - 1]?.title ||
                                              stops[i - 1]?.address;
                                    return (
                                        <div key={i} style={{ padding: "4px 0" }}>
                                            <div className="gt-body-sm">
                                                <PushpinOutlined
                                                    style={{
                                                        marginRight: 6,
                                                        color: "var(--gt-accent)",
                                                    }}
                                                />
                                                <strong>
                                                    {s.title || s.address}
                                                </strong>
                                            </div>
                                            {(stopMaps ||
                                                stopFromCurrent ||
                                                stopRouteLink) && (
                                                <div
                                                    className="pa-chip-row"
                                                    style={{
                                                        marginLeft: 22,
                                                        marginTop: 6,
                                                    }}
                                                >
                                                    {stopMaps && (
                                                        <a
                                                            className="pa-chip-btn pa-chip-btn--map pa-chip-btn--icon"
                                                            href={stopMaps}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Show on map"
                                                            aria-label="Show on map"
                                                        >
                                                            <MapPinIcon />
                                                        </a>
                                                    )}
                                                    {stopFromCurrent && (
                                                        <a
                                                            className="pa-chip-btn pa-chip-btn--route pa-chip-btn--icon"
                                                            href={stopFromCurrent}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Directions from current location"
                                                            aria-label="Directions from current location"
                                                        >
                                                            <NavigateIcon />
                                                        </a>
                                                    )}
                                                    {stopRouteLink &&
                                                        stopOriginLabel && (
                                                            <a
                                                                className="pa-chip-btn pa-chip-btn--route"
                                                                href={stopRouteLink}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                From {stopOriginLabel}
                                                            </a>
                                                        )}
                                                </div>
                                            )}
                                            {s.address && s.title && (
                                                <div
                                                    style={{
                                                        whiteSpace: "pre-wrap",
                                                        marginLeft: 22,
                                                        fontSize: 12,
                                                        color: "var(--gt-text-muted)",
                                                    }}
                                                >
                                                    {s.address}
                                                </div>
                                            )}
                                            {s.notes && (
                                                <div
                                                    style={{
                                                        whiteSpace: "pre-wrap",
                                                        marginLeft: 22,
                                                        fontSize: 12,
                                                        color: "var(--gt-text-muted)",
                                                    }}
                                                >
                                                    {s.notes}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {showAccommodation && (
                            <aside className="gt-tour-hotel gt-tour-hotel--stay">
                                <div className="gt-tour-hotel__eyebrow">
                                    <HomeOutlined /> Where you'll stay
                                </div>
                                <h4 className="gt-tour-hotel__name">
                                    {accommodation.title ||
                                        accAddress ||
                                        "Overnight stop"}
                                </h4>
                                {accAddress && accommodation.title && (
                                    <p
                                        className="gt-tour-hotel__body"
                                        style={{ whiteSpace: "pre-wrap" }}
                                    >
                                        {accAddress}
                                    </p>
                                )}
                                {accommodation.notes && (
                                    <p
                                        className="gt-tour-hotel__body"
                                        style={{ whiteSpace: "pre-wrap" }}
                                    >
                                        {accommodation.notes}
                                    </p>
                                )}
                                {(accommodationMapsLink ||
                                    accommodationFromCurrent ||
                                    drivingLink ||
                                    accommodation.url) && (
                                    <div
                                        className="pa-chip-row"
                                        style={{ marginTop: 4 }}
                                    >
                                        {accommodationMapsLink && (
                                            <a
                                                className="pa-chip-btn pa-chip-btn--map pa-chip-btn--icon"
                                                href={accommodationMapsLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Show on map"
                                                aria-label="Show on map"
                                            >
                                                <MapPinIcon />
                                            </a>
                                        )}
                                        {accommodationFromCurrent && (
                                            <a
                                                className="pa-chip-btn pa-chip-btn--route pa-chip-btn--icon"
                                                href={accommodationFromCurrent}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Directions from current location"
                                                aria-label="Directions from current location"
                                            >
                                                <NavigateIcon />
                                            </a>
                                        )}
                                        {drivingLink && (
                                            <a
                                                className="pa-chip-btn pa-chip-btn--route"
                                                href={drivingLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                From {drivingOriginLabel}
                                            </a>
                                        )}
                                        {accommodation.url && (
                                            <a
                                                className="pa-chip-btn pa-chip-btn--map"
                                                href={accommodation.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                🔗 Accommodation link
                                            </a>
                                        )}
                                    </div>
                                )}
                                {!accommodation.url && accAddress && (
                                    <>
                                        <div
                                            style={{
                                                fontSize: 11.5,
                                                color: "var(--gt-text-muted)",
                                                marginTop: 8,
                                                marginBottom: 6,
                                            }}
                                        >
                                            🔎 Find a place to stay
                                        </div>
                                        <div className="pa-chip-row">
                                            {stayBookingLink && (
                                                <a
                                                    className="pa-search-chip pa-search-chip--booking"
                                                    href={stayBookingLink}
                                                    target="_blank"
                                                    rel="sponsored noopener noreferrer"
                                                >
                                                    Booking.com
                                                </a>
                                            )}
                                            {stayHotelsLink && (
                                                <a
                                                    className="pa-search-chip pa-search-chip--hotels"
                                                    href={stayHotelsLink}
                                                    target="_blank"
                                                    rel="sponsored noopener noreferrer"
                                                >
                                                    Hotels.com
                                                </a>
                                            )}
                                            {stayPitchupLink && (
                                                <a
                                                    className="pa-search-chip"
                                                    href={stayPitchupLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Pitchup
                                                </a>
                                            )}
                                            {stayAirbnbLink && (
                                                <a
                                                    className="pa-search-chip"
                                                    href={stayAirbnbLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Airbnb
                                                </a>
                                            )}
                                        </div>
                                    </>
                                )}
                            </aside>
                        )}
                    </GuideSection>
                );
            })}

            {/* 5 — ★ What you get when you clone */}
            <GuideSection
                index={1}
                heading="What you get when you clone"
                intro="Cloning drops the whole template into your own tours — nothing is locked. Change a single overnight or rebuild the route from scratch; it's yours."
                highlight={{
                    glyph: "★",
                    background: "var(--gt-accent)",
                    color: "#3a2a0a",
                }}
                items={CLONE_BENEFITS}
            />

            {/* 6 — Clone CTA + curator credit */}
            <GuideCtaBand
                eyebrow="Ready to ride it?"
                heading={`Clone ${template.name} into your tours.`}
                body="Free to clone. Set your dates, invite your mates, and make every leg your own."
                primary={{ label: "Clone to my tours", onClick: handleClone }}
                secondary={{ label: "Browse more tours", to: "/explore" }}
                footnote={
                    template.authorName
                        ? `Curated by ${template.authorName}`
                        : undefined
                }
            />

            {/* 7 — Related guides */}
            <div className="gt-guide-article gt-guide-article--trailing">
                <RelatedGuides
                    heading="Useful guides for this trip"
                    slugs={[
                        "packing-europe",
                        "french-toll-roads",
                        "fuel-prices",
                        "bike-security",
                    ]}
                />
            </div>

            <ItineraryMap
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                days={mapDays}
                tourName={template.name}
            />
        </>
    );
}

export default TourTemplate;
